'use client'

import * as React from 'react'
import { cn } from '../../utils'

export interface SegmentedPickerOption<T extends string> {
  value: T
  label: React.ReactNode
  /** Optional leading icon (component, not element). */
  icon?: React.ComponentType<{ className?: string }>
  /** Per-option active icon class (e.g. tint colors). */
  activeIconClassName?: string
  /** Per-option disabled state. */
  disabled?: boolean
}

export interface SegmentedPickerProps<T extends string> {
  value: T
  options: SegmentedPickerOption<T>[]
  onChange: (value: T) => void
  /** Optional label rendered above the picker. */
  label?: string
  ariaLabel?: string
  className?: string
  /** When true, only show the icons without text labels. */
  iconsOnly?: boolean
}

/**
 * Generic segmented pill picker — visually unified control used by the
 * theme toggle and language switcher inside the profile dropdown. Pure
 * presentation; consumer owns the value/onChange.
 */
export function SegmentedPicker<T extends string>({
  value,
  options,
  onChange,
  label,
  ariaLabel,
  className,
  iconsOnly,
}: SegmentedPickerProps<T>) {
  return (
    <div className={cn('w-full', className)}>
      {label && (
        <div className="px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
      )}
      <div
        role="radiogroup"
        aria-label={ariaLabel ?? label}
        className="grid gap-1 rounded-full border border-border bg-muted/60 p-1"
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
      >
        {options.map((opt) => {
          const active = opt.value === value
          const Icon = opt.icon
          const labelString = typeof opt.label === 'string' ? opt.label : undefined
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={labelString}
              title={labelString}
              disabled={opt.disabled}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!opt.disabled) onChange(opt.value)
              }}
              className={cn(
                'group relative inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full px-2 py-1.5',
                'text-xs font-medium transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:opacity-40 disabled:pointer-events-none',
                active
                  ? 'bg-card text-foreground shadow-sm ring-1 ring-border/60'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {Icon && (
                <Icon
                  className={cn(
                    'h-3.5 w-3.5 shrink-0 transition-transform duration-300',
                    active && opt.activeIconClassName,
                  )}
                />
              )}
              {!iconsOnly && <span className="truncate">{opt.label}</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}
